import { ref, readonly } from 'vue'
import { useNotifications } from './useNotifications'
import { useAuth } from './useAuth'

export interface ApiError {
  message: string
  isNetworkError: boolean
  status?: number
  code?: string
}

// Function to get the correct API base URL
const getApiBaseUrl = (): string => {
  // Check if we're running on localhost (development)
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return '/api' // Use /api prefix for Vite proxy
  }
  // Production: use the deployed Cloudflare Worker URL
  return 'https://delivery-area.mwsemple.workers.dev'
}

export function useApi() {
  const loading = ref(false)
  const { showError, showSuccess } = useNotifications()
  const { getToken } = useAuth()

  const handleApiError = (error: unknown, response?: Response): ApiError => {
    console.error('API Error:', error)

    // Check for specific HTTP status codes with helpful messages
    if (response) {
      switch (response.status) {
        case 400:
          return {
            message: 'Invalid request. Please check your input and try again.',
            isNetworkError: false,
            status: 400,
            code: 'BAD_REQUEST'
          }
        case 404:
          return {
            message: 'The requested resource was not found. It may have been deleted or moved.',
            isNetworkError: false,
            status: 404,
            code: 'NOT_FOUND'
          }
        case 409:
          return {
            message: 'This action conflicts with the current state. Please refresh and try again.',
            isNetworkError: false,
            status: 409,
            code: 'CONFLICT'
          }
        case 422:
          return {
            message: 'The data provided is invalid. Please check your input.',
            isNetworkError: false,
            status: 422,
            code: 'VALIDATION_ERROR'
          }
        case 500:
          return {
            message: 'A server error occurred. Please try again in a moment.',
            isNetworkError: false,
            status: 500,
            code: 'SERVER_ERROR'
          }
        case 503:
          return {
            message: 'The service is temporarily unavailable. Please try again later.',
            isNetworkError: true,
            status: 503,
            code: 'SERVICE_UNAVAILABLE'
          }
      }
    }

    // Check for network/connection errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        message: 'Connection failed. Please check your internet connection and try again.',
        isNetworkError: true,
        code: 'NETWORK_ERROR'
      }
    }

    // Check for fetch errors
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          message: 'Request timed out. Please try again.',
          isNetworkError: true,
          code: 'TIMEOUT'
        }
      }

      // Enhanced network error detection
      const networkErrorPatterns = [
        'NetworkError',
        'Failed to fetch',
        'Failed to load resource',
        'network connection was lost',
        'connection was lost',
        'access control checks',
        'CORS',
        'net::ERR_',
        'ERR_NETWORK',
        'ERR_INTERNET_DISCONNECTED',
        'ERR_CONNECTION_REFUSED',
        'ERR_CONNECTION_RESET',
        'ERR_CONNECTION_TIMED_OUT'
      ]

      const isNetworkError = networkErrorPatterns.some(pattern => 
        error.message.toLowerCase().includes(pattern.toLowerCase())
      )

      if (isNetworkError) {
        return {
          message: 'Unable to connect to the server. Please check your connection.',
          isNetworkError: true,
          code: 'CONNECTION_ERROR'
        }
      }

      // Check for specific business logic errors
      if (error.message.includes('Insufficient stock')) {
        return {
          message: `${error.message} Please check available inventory or contact support.`,
          isNetworkError: false,
          code: 'INSUFFICIENT_STOCK'
        }
      }

      if (error.message.includes('No price set')) {
        return {
          message: `${error.message} Please set prices in the Price List section before creating orders.`,
          isNetworkError: false,
          code: 'MISSING_PRICE'
        }
      }

      if (error.message.includes('Unknown ingredient') || error.message.includes('Invalid ingredient')) {
        return {
          message: `${error.message} Please check the Inventory section to see available ingredients.`,
          isNetworkError: false,
          code: 'INVALID_INGREDIENT'
        }
      }
    }

    // Default error
    return {
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
      isNetworkError: false
    }
  }

  const makeRequest = async <T>(
    url: string,
    options: RequestInit = {},
    successMessage?: string,
    enableRetry: boolean = true,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<T> => {
    const executeRequest = async (): Promise<T> => {
      loading.value = true
      let response: Response | undefined

      try {
        // Get authentication token
        const token = await getToken()
        
        // Get the correct base URL and construct full URL
        const baseUrl = getApiBaseUrl()
        const fullUrl = baseUrl + url
        
        // Add timeout to detect connection issues
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...options.headers as Record<string, string>,
        }

        // Add authorization header if token is available
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        response = await fetch(fullUrl, {
          ...options,
          signal: controller.signal,
          headers,
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const errorMessage = errorData.error || `Server error: ${response.status} ${response.statusText}`
          
          const error = new Error(errorMessage)
          const apiError = handleApiError(error, response)
          throw apiError
        }

        const data = await response.json()
        
        if (successMessage) {
          showSuccess(successMessage)
        }

        return data
      } catch (error) {
        // If error is already an ApiError (from above), re-throw it
        if (error && typeof error === 'object' && 'isNetworkError' in error) {
          throw error
        }
        
        // Handle other errors (network, parsing, etc.)
        const apiError = handleApiError(error, response)
        throw apiError
      } finally {
        loading.value = false
      }
    }

    // Use retry logic if enabled
    if (enableRetry) {
      return retry(executeRequest, maxRetries, retryDelay)
    } else {
      try {
        return await executeRequest()
      } catch (error) {
        const apiError = error as ApiError
        showError(apiError.message)
        throw error
      }
    }
  }

  const get = async <T>(url: string, enableRetry: boolean = true): Promise<T> => {
    return makeRequest<T>(url, { method: 'GET' }, undefined, enableRetry)
  }

  const post = async <T>(url: string, data: any, successMessage?: string, enableRetry: boolean = true): Promise<T> => {
    return makeRequest<T>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    }, successMessage, enableRetry)
  }

  const patch = async <T>(url: string, data?: any, successMessage?: string, enableRetry: boolean = true): Promise<T> => {
    return makeRequest<T>(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }, successMessage, enableRetry)
  }

  const del = async <T>(url: string, successMessage?: string, enableRetry: boolean = true): Promise<T> => {
    return makeRequest<T>(url, {
      method: 'DELETE',
    }, successMessage, enableRetry)
  }

  const retry = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        const apiError = error as ApiError
        
        if (attempt === maxRetries || !apiError.isNetworkError) {
          throw error
        }

        showError(`Connection failed. Retrying... (${attempt}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, delay * attempt))
      }
    }

    throw new Error('Max retries exceeded')
  }

  return {
    loading: readonly(loading),
    get,
    post,
    patch,
    del,
    retry,
    makeRequest
  }
} 